/**
 * Client-side storage for the "trust this device" token.
 *
 * The server already implemented both halves of this: `POST /auth/otp/verify`
 * with `trustDevice: true` mints a token and returns it as `data.deviceToken`,
 * and `POST /auth/login` skips the OTP step when a matching token is supplied.
 * Nothing in the client ever read or sent it, so ticking the box created a
 * `trusted_devices` row that was immediately orphaned and every subsequent
 * login still demanded a code.
 *
 * Keyed per account so two people signing in on the same shared machine each
 * get their own trust, and one person's trusted device never lets another skip
 * verification.
 *
 * Stored in localStorage, consistent with how the app already persists the JWT
 * pair. That means it is readable by any script running on the origin — the
 * same exposure the access token already carries. A cookie marked HttpOnly
 * would be stronger, but the API returns the token in the response body, so
 * moving it would be a server-side change too.
 */

const PREFIX = 'trusted-device:';

interface StoredDevice {
  token: string;
  expiresAt: string;
}

const keyFor = (email: string) => `${PREFIX}${email.trim().toLowerCase()}`;

/**
 * The stored token for this account, or null when absent, unreadable, or past
 * its expiry. Expired entries are dropped on read so they cannot pile up.
 */
export const getTrustedDeviceToken = (email: string): string | null => {
  if (!email) return null;
  try {
    const raw = localStorage.getItem(keyFor(email));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredDevice;
    if (!parsed?.token) return null;

    // The server enforces expiry too; checking here just avoids a pointless
    // round-trip with a token we already know is dead.
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(keyFor(email));
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
};

export const saveTrustedDeviceToken = (
  email: string,
  token: string,
  expiresAt: string,
): void => {
  if (!email || !token) return;
  try {
    localStorage.setItem(keyFor(email), JSON.stringify({ token, expiresAt }));
  } catch {
    // Private browsing or a full quota — trust simply won't persist.
  }
};

/** Forget this account's device trust, e.g. after the server rejected it. */
export const clearTrustedDeviceToken = (email: string): void => {
  if (!email) return;
  try {
    localStorage.removeItem(keyFor(email));
  } catch {
    /* nothing useful to do */
  }
};

/**
 * Drop every stored device token. Pairs with the server's
 * `POST /auth/trusted-devices/revoke-all`, so revoking centrally also clears
 * the local copy instead of leaving a token that silently fails on next login.
 */
export const clearAllTrustedDeviceTokens = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* nothing useful to do */
  }
};
